import { AiFieldType, SettingsPageGetDueDiligenceFunnelQuery } from '@/__generated__/graphql';
import CheckBox from '@/components/basicComponents/checkbox';
import Input from '@/components/basicComponents/input';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  CREATE_DUE_DILIGENCE_FIELD_MUTATION,
  GET_FUNNEL_QUERY,
  UPDATE_DRAFT_DUE_DILIGENCE_FIELD_MUTATION,
  UPDATE_DUE_DILIGENCE_FIELD_MUTATION
} from '../queries';

interface AddFieldProps {
  onClose: () => void;
  isOpen: boolean;
  currentField: SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['sections'][number]['fields'][number] | null;
  tiersData: SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'];
  currentId: string;
  sectionId: string | null;
  numberOfFields: number;
  isLimitedEdits: boolean;
}

export const fieldsType = {
  JSON: 'JSON',
  LONG_FORM_CONTENT: 'Long Form Content',
  SHORT_TEXT: 'Short Text',
  DATE: 'Date',
  DOLLAR: 'Dollar Amount',
  NUMBER: 'Number',
  PERCENTAGE: 'Percentage'
};

const initialValues = {
  value: '',
  selectedFieldType: 'Select',
  selectLinkedField: 'None',
  isFieldRequired: true
};

const AddField = ({ onClose, isOpen, tiersData, currentField, currentId, sectionId, numberOfFields, isLimitedEdits }: AddFieldProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [formValues, setFormValues] = useState(initialValues);
  const [linkedFieldList, setLinkedFieldList] = useState<{ value: string; id?: string }[]>([]);
  const [chosenLinkedFieldId, setChosenLinkedFieldId] = useState<string[]>([]);

  const [createField] = useMutation(CREATE_DUE_DILIGENCE_FIELD_MUTATION, {
    refetchQueries: () => [
      {
        query: GET_FUNNEL_QUERY,
        variables: {
          id: queryParams.get('funnelId')
        }
      }
    ]
  });
  const [updateField] = useMutation(UPDATE_DUE_DILIGENCE_FIELD_MUTATION, {
    refetchQueries: () => [
      {
        query: GET_FUNNEL_QUERY,
        variables: {
          id: queryParams.get('funnelId')
        }
      }
    ]
  });
  const [updateDraftField] = useMutation(UPDATE_DRAFT_DUE_DILIGENCE_FIELD_MUTATION, {
    refetchQueries: () => [
      {
        query: GET_FUNNEL_QUERY,
        variables: {
          id: queryParams.get('funnelId')
        }
      }
    ]
  });

  useEffect(() => {
    const newTiersData: { value: string; id?: string }[] = [];
    const newChosenLinedId: string[] = [];

    tiersData.forEach((tier) => {
      tier.sections.forEach((section) => {
        section.fields.forEach((field) => {
          if (getKeyByValue(fieldsType, formValues.selectedFieldType) === field.type) {
            const formattedString = {
              value: `${tier.name} - ${section.name} - ${field.name}`,
              id: field.id
            };
            newTiersData.push(formattedString);
          }
          if (field.linkedFieldId) {
            newChosenLinedId.push(field.linkedFieldId);
            newChosenLinedId.push(field.id);
          }
        });
      });
    });

    if (currentField) {
      newChosenLinedId.push(currentField.id);
      setFormValues({
        ...formValues,
        selectLinkedField: newTiersData.find((linkedField) => linkedField.id === currentField.linkedFieldId)?.value || 'None'
      });
    }
    setChosenLinkedFieldId(newChosenLinedId);
    setLinkedFieldList(newTiersData);
  }, [tiersData, formValues.selectedFieldType]);

  useEffect(() => {
    if (currentField) {
      const { name, type, linkedFieldId, required } = currentField;
      setFormValues({
        value: name,
        selectedFieldType: fieldsType[type as keyof typeof fieldsType] || 'Select',
        selectLinkedField: linkedFieldList.find((linkedField) => linkedField.id === linkedFieldId)?.value || 'None',
        isFieldRequired: required
      });
    } else {
      setFormValues(initialValues);
    }
  }, [currentField]);

  const getKeyByValue = (object: { [key in keyof typeof fieldsType]: string }, value: string) => {
    return Object.keys(object).find((key) => object[key as keyof typeof fieldsType] === value);
  };

  const saveValue = () => {
    if (currentField && isLimitedEdits) {
      updateField({
        variables: {
          data: {
            id: currentId,
            name: formValues.value
          }
        },
        onCompleted: () => {
          onClose();
        }
      });

      return;
    }

    if (currentField) {
      updateDraftField({
        variables: {
          data: {
            id: currentId,
            name: formValues.value,
            type: getKeyByValue(fieldsType, formValues.selectedFieldType) as AiFieldType,
            linkedFieldId: linkedFieldList.find((linkedField) => linkedField.value === formValues.selectLinkedField)?.id ?? null,
            required: formValues.isFieldRequired
          }
        }
      });
    } else {
      createField({
        variables: {
          data: {
            funnelTierSectionId: sectionId,
            name: formValues.value,
            order: numberOfFields + 1,
            type: getKeyByValue(fieldsType, formValues.selectedFieldType) as AiFieldType,
            linkedFieldId: linkedFieldList.find((linkedField) => linkedField.value === formValues.selectLinkedField)?.id ?? null,
            required: formValues.isFieldRequired
          }
        }
      });
    }

    onClose();
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title={currentField ? 'Edit Field' : 'Add New Field'}
      confirmButton={{
        name: 'Save',
        onClick: saveValue,
        disabled: !formValues.value || formValues.selectedFieldType === 'Select'
      }}
      showRequiredFields
    >
      <ModalContainer>
        <Input
          value={formValues.value}
          onChange={(event) => setFormValues({ ...formValues, value: event.target.value })}
          label="Give this Field a name"
          type="text"
          placeholder="Give this Field a unique name"
          size="lg"
          isRequiredField
          autoFocus
        />
        <Select
          value={formValues.selectedFieldType}
          setValue={(value) => setFormValues({ ...formValues, selectedFieldType: value })}
          options={Object.values(fieldsType).map((value) => createSimpleSelectOption(value))}
          styleType={currentField ? 'disabled' : 'default'}
          label="Field Type"
          width="100%"
          size="lg"
          isRequiredField
        />
        <Select
          value={formValues.selectLinkedField}
          setValue={(value) => setFormValues({ ...formValues, selectLinkedField: value })}
          options={['None', ...linkedFieldList.filter((item) => item.id && !chosenLinkedFieldId.includes(item.id)).map((item) => item.value)].map(
            (item) => createSimpleSelectOption(item)
          )}
          styleType={isLimitedEdits ? 'disabled' : 'default'}
          label="Linked Field"
          width="100%"
          size="lg"
        />
        <CheckBoxWrap
          id="requiredField"
          onClick={
            isLimitedEdits
              ? () => null
              : () =>
                  setFormValues({
                    ...formValues,
                    isFieldRequired: !formValues.isFieldRequired
                  })
          }
          isDisabled={isLimitedEdits}
        >
          <CheckBox id="requiredField" isActive={formValues.isFieldRequired} />
          Make Field Required
        </CheckBoxWrap>
      </ModalContainer>
    </ModalWrappedContent>
  );
};

export default AddField;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const CheckBoxWrap = styled.div<{ isDisabled: boolean }>`
  margin-top: 20px;
  padding: 18px;
  border-top: 1px solid ${themeVar('border.base')};
  display: flex;
  gap: 10px;
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-weight: 400;
  cursor: ${({ isDisabled }) => (isDisabled ? 'auto' : 'pointer')};
  opacity: ${({ isDisabled }) => (isDisabled ? '.5' : '1')};

  &:hover #requiredField {
    cursor: ${({ isDisabled }) => (isDisabled ? 'auto' : 'pointer')};
  }
`;
